import type { Attribute, Product, Variant } from '@nucleus-commerce/api-client/admin';
import type { Money } from '@nucleus-commerce/core';

import type { OptionAxisForm, OptionValueForm } from '../schemas/product-form-schema';

/**
 * What saving the options will do to the variants, worked out on the client so the Options card
 * can say so before the save. It follows `planOptionSync` in the backend (D-188), rule for rule:
 * keep this in step with it, because the numbers are a promise to the person saving.
 */

type SavedAxis = Product['options'][number];
export type PreviewAttribute = Pick<Attribute, 'key' | 'label' | 'values'>;
type PreviewVariant = Pick<Variant, 'status' | 'position' | 'optionValues'>;

export function dictionaryValue(attribute: PreviewAttribute | undefined, value: OptionValueForm) {
  return value.attributeValueId
    ? attribute?.values.find((v) => v.id === value.attributeValueId)
    : undefined;
}

export function optionValueLabel(
  attribute: PreviewAttribute | undefined,
  value: OptionValueForm,
): string {
  const own = value.label?.trim();
  if (own) return own;
  return dictionaryValue(attribute, value)?.label ?? value.value ?? '?';
}

/** The stored code of a form value: what a variant's `optionValues` holds. Null if unknown yet. */
export function optionValueStoredCode(
  value: OptionValueForm,
  savedAxis: SavedAxis | undefined,
  attribute: PreviewAttribute | undefined,
): string | null {
  if (value.value) return value.value;
  if (!value.attributeValueId) return null;
  return (
    savedAxis?.values.find((v) => v.attributeValueId === value.attributeValueId)?.value ??
    dictionaryValue(attribute, value)?.value ??
    null
  );
}

const byPosition = <T extends { position: number }>(rows: readonly T[]): T[] =>
  [...rows].sort((a, b) => a.position - b.position);

/**
 * The price new combinations start at on a saved product: the first variant's, an archived one's
 * only when nothing else is left. Null without any variant: there is nothing to take a price from.
 */
export function syncBasePrice(
  variants: ReadonlyArray<Pick<Variant, 'status' | 'position' | 'price'>>,
): Money | null {
  const sorted = byPosition(variants);
  return (sorted.find((v) => v.status !== 'archived') ?? sorted[0])?.price ?? null;
}

export interface OptionSyncCounts {
  before: number;
  after: number;
  created: number;
  archived: number;
}

export interface OptionChangePreview {
  /** Null when a count can't be known exactly: the variants or a dictionary are still loading. */
  counts: OptionSyncCounts | null;
  /** An option or a value was added or removed. */
  valuesChanged: boolean;
  reordered: boolean;
  renamed: boolean;
  /** New options whose first value the existing variants take on. */
  firstValues: Array<{ option: string; value: string }>;
  /** Whole options removed. */
  removedOptions: string[];
  /** Option names in the order shoppers will see them. */
  order: string[];
}

interface CodedAxis {
  key: string;
  values: string[];
}

const signatureOf = (optionValues: Record<string, string>): string =>
  Object.keys(optionValues)
    .sort()
    .map((key) => `${key}=${optionValues[key]}`)
    .join('&');

function countSync(
  before: readonly CodedAxis[],
  after: readonly CodedAxis[],
  variants: readonly PreviewVariant[],
): OptionSyncCounts {
  const allowed = new Map(after.map((axis) => [axis.key, new Set(axis.values)]));
  const active = byPosition(variants).filter((row) => row.status !== 'archived');

  const kept = new Map<string, Record<string, string>>();
  let archived = 0;
  for (const row of active) {
    const stored = row.optionValues;
    const valueRemoved = Object.entries(stored).some(
      ([key, value]) => allowed.get(key)?.has(value) === false,
    );
    const optionValues = Object.fromEntries(
      after.map((axis) => [axis.key, stored[axis.key] ?? axis.values[0] ?? '']),
    );
    const signature = signatureOf(optionValues);
    if (valueRemoved || kept.has(signature)) archived += 1;
    else kept.set(signature, optionValues);
  }

  const known = new Map(before.map((axis) => [axis.key, new Set(axis.values)]));
  // Kept variants already stand on a new option's first value, so that value alone brings nothing.
  const firstValueTaken = kept.size > 0;
  const isNew = (axis: CodedAxis, value: string): boolean => {
    const values = known.get(axis.key);
    if (values) return !values.has(value);
    return !(firstValueTaken && axis.values[0] === value);
  };
  const combinations = after.reduce((total, axis) => total * axis.values.length, 1);
  const withoutNew = after.reduce(
    (total, axis) => total * axis.values.filter((value) => !isNew(axis, value)).length,
    1,
  );
  const keptUsingNew = [...kept.values()].filter((optionValues) =>
    after.some((axis) => isNew(axis, optionValues[axis.key] ?? '')),
  ).length;
  const created = combinations - withoutNew - keptUsingNew;
  return { before: active.length, after: kept.size + created, created, archived };
}

const sameOrder = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((item, index) => item === b[index]);

/** Compares the form's options with the saved ones, as the backend will on save. */
export function previewOptionChange(
  saved: readonly SavedAxis[],
  options: readonly OptionAxisForm[],
  attributesById: ReadonlyMap<string, PreviewAttribute>,
  variants: readonly PreviewVariant[] | undefined,
): OptionChangePreview {
  const savedByAttribute = new Map(saved.map((axis) => [axis.attributeId, axis]));
  const nameOf = (axis: OptionAxisForm) =>
    axis.name.trim() ||
    (attributesById.get(axis.attributeId)?.label ??
      savedByAttribute.get(axis.attributeId)?.name ??
      axis.attributeId);

  let exact = variants !== undefined;
  const coded = options.map((axis) => {
    const savedAxis = savedByAttribute.get(axis.attributeId);
    const attribute = attributesById.get(axis.attributeId);
    const key = savedAxis?.key ?? attribute?.key;
    if (!key) exact = false;
    const values = axis.values.flatMap((value) => {
      const code = optionValueStoredCode(value, savedAxis, attribute);
      if (code === null) exact = false;
      return code === null ? [] : [code];
    });
    return { axis, savedAxis, attribute, key: key ?? axis.attributeId, values };
  });

  const removedOptions = saved
    .filter((axis) => !options.some((o) => o.attributeId === axis.attributeId))
    .map((axis) => axis.name);
  const addedOptions = coded.filter((entry) => !entry.savedAxis);
  const valuesChanged =
    removedOptions.length > 0 ||
    addedOptions.length > 0 ||
    coded.some(({ savedAxis, values }) => {
      const stored = savedAxis?.values.map((v) => v.value) ?? [];
      return values.length !== stored.length || values.some((value) => !stored.includes(value));
    });

  const sharedNow = coded.filter((entry) => entry.savedAxis).map((entry) => entry.key);
  const sharedThen = saved.map((axis) => axis.key).filter((key) => sharedNow.includes(key));
  const reordered =
    !sameOrder(sharedNow, sharedThen) ||
    coded.some(({ savedAxis, values }) => {
      if (!savedAxis) return false;
      const stored = savedAxis.values.map((v) => v.value);
      return !sameOrder(
        values.filter((value) => stored.includes(value)),
        stored.filter((value) => values.includes(value)),
      );
    });

  const renamed = coded.some(({ axis, savedAxis, attribute, values }) => {
    if (!savedAxis) return false;
    if (axis.name.trim() !== '' && axis.name.trim() !== savedAxis.name) return true;
    return axis.values.some((value, index) => {
      const stored = savedAxis.values.find((v) => v.value === values[index]);
      return stored !== undefined && optionValueLabel(attribute, value) !== stored.label;
    });
  });

  // An option without values blocks the save anyway; it counts as not there yet.
  const after = coded.filter((entry) => entry.values.length > 0);
  const counts =
    exact && variants
      ? countSync(
          saved.map((axis) => ({ key: axis.key, values: axis.values.map((v) => v.value) })),
          after,
          variants,
        )
      : null;

  // Only variants that survive the save take a new option's first value.
  const anyKept = counts
    ? counts.after > counts.created
    : (variants?.some((row) => row.status !== 'archived') ?? true);
  const firstValues = anyKept
    ? addedOptions.flatMap(({ axis, attribute }) => {
        const first = axis.values[0];
        return first ? [{ option: nameOf(axis), value: optionValueLabel(attribute, first) }] : [];
      })
    : [];

  return {
    counts,
    valuesChanged,
    reordered,
    renamed,
    firstValues,
    removedOptions,
    order: options.map(nameOf),
  };
}
